import {
  Heading,
  Text,
  ListItem,
	Image,
} from "@chakra-ui/react";
import {
  SectionIndex,
  SectionTitle,
  LinkToSection,
} from "../../../new_component/organisms/SectionIndex";
import {
  WikiArticle,
  WikiArticleMain,
  WikiArticleSubPanel,
} from "../WikiArticle";
import CodeExample from "../../../new_component/organisms/CodeExample";
import db_ref from "../../../resources/db_reference.png";
import { Quote, Author } from "./Introduction";


const PathInference = () => (
  <WikiArticle>
    <WikiArticleMain>
      <section>
        <Heading as="h2">Path Inference</Heading>
        <Quote style={{textAlign:"end"}}>
        SQL, Lisp, and Haskell are the only programming languages that I've seen where one spends more time thinking than typing.
          <Author>Philip Greenspun</Author>
        </Quote>
        <Text>
        Isn't it odd that IT people needed to write obvious things and logic? 
        Wasn't the idea of programming precisely to avoid unnecessary and repetitive efforts? 
        If the logic was clear, why the need to be specific to a point that is redundant and verbose? 
        Sure, things can change in the future, and that's why FQL keeps the unambiguous full-path when a path is inferred. 
        But humans do not need to think in such basic, lengthy, and intrincated terms. 
        </Text>
      </section>

      <section>
        <SectionTitle>It's Simple</SectionTitle>
        <Text>
        Reusing the same example from the introduction, imagine you have some data about Countries, 
        Cities (and the Country they belong to), Companies (and the City where the HQ is), 
        Products (and the Company that produces them), and finally some invoices that contain multiple Products. 
        In SQL, each of these entities will be represented by a Table. In an Application Base, each of them will be a Form.
        </Text>
          <Image
            alignSelf={"center"}
            margin={"auto"}
            borderRadius={"7px"}
            src={db_ref}
            alt="DB References"
          />
          <br />

					  <Text>
            But then, if you want to see something like "Invoices containing
            Products from Uruguay", have a look at how you would write it in SQL:
          </Text>
					<CodeExample margin={0} title="SQL" run={false}>
            {`\
SELECT *                                                      
FROM invoices i 
INNER JOIN invoice_lines il
ON i.nr = il.invoice_nr
INNER JOIN products p
ON il.product_id = p.id
INNER JOIN companies co
ON p.company_id = co.id
INNER JOIN cities ci
ON co.city_id = ci.id
INNER JOIN countries c
ON ci.country_id = c.id
WHERE c.name = 'URUGUAY'
              `}
          </CodeExample>

					<Text>
						And the equivalent in FQL:
					</Text>

          <CodeExample margin={0} title="FQL" run={false}>
            {`\
GET invoices 
  WITH country.name = 'Uruguay'
              `}
          </CodeExample>
          <Text>
            This is achieved through an internal Data Dictionary, and a graph
            search that (if non-ambiguous) makes the statement valid. We call it "Path Inference".
          </Text>
      </section>

      <section>
        <SectionTitle>Previous commands</SectionTitle>
        <Text>
          Let's create some forms to demonstrate the use of path inference.
        </Text>
        <CodeExample title="fql (create form)" multiline>{`\
create form Countries(name text)
create form Cities(name text, country references Countries.name)
create form Persons(name text, city references Cities.name)
create form Store(name text, location references Cities.name, employees references 1..many Persons.name)
create form Products (name text, store references store.name)

create new Countries values ('Argentina')
create new Countries values ('Japan')
create new Countries values ('Uruguay')

create new Cities ('Buenos Aires', 'Argentina')
create new Cities ('Tokyo', 'Japan')
create new Cities ('Montevideo', 'Uruguay')

create new Persons ('Leandro', 'Buenos Aires')
create new Persons ('Felix', 'Buenos Aires')
create new Persons ('Eitaro', 'Tokyo')
create new Persons ('Fernando', 'Montevideo')
`}
        </CodeExample>
      </section>

      <section>
         <SectionTitle>Examples</SectionTitle>
        <Text>
          Now, if we define a rule to avoid users from Argentina, we can see how easy is to do it
					without having to follow the whole path to Countries.
        </Text>
        <CodeExample title="fql (validations)">
					{`\
define rule NO_USERS_FROM_ARGENTINA on PERSONS when Country.name = 'Argentina' is invalid
`}
        </CodeExample>

        <Text>
          But if we try to get a property through an ambiguos path, we will get an error.
        </Text>
        <CodeExample title="fql (validations)" multiline>
					{`\
define rule CONFLICT_WITH_PATHS on Store when country.name = 'Argentina' is invalid
`}
        </CodeExample>
      </section>

      <section>
        <SectionTitle>Test some FQL code!</SectionTitle>
        <Text>
          Feel free to test some code!
        </Text>
        <CodeExample title="fql (validations)" minRows={5} />
      </section>

    </WikiArticleMain>
    <WikiArticleSubPanel>
      <SectionIndex>
       <ListItem><LinkToSection>It's Simple</LinkToSection></ListItem>
       <ListItem><LinkToSection>Previous Commands</LinkToSection></ListItem> 
        <ListItem><LinkToSection>Examples</LinkToSection></ListItem>
        <ListItem><LinkToSection>Test some FQL code!</LinkToSection></ListItem> 
      </SectionIndex>
    </WikiArticleSubPanel>
  </WikiArticle>
);

export default PathInference;
